/**
 * 学习板块 TTS「听书」播放控制器（Zustand，App 独占）
 * 职责：分句/分章队列推进、播放状态机、锁屏播控响应、自动读下一章、逐句完成事件对位。
 * 设计：
 *  - 合成走原生系统引擎（@shared/api/tts 桥），本 store 只管「播什么/进度/权益门控/连章」；
 *  - 每句 utteranceId = `${sessionId}:${chapterId}:${index}`，完成事件带此前缀对位；
 *    stop/切章时 sessionId++ → 陈旧事件被忽略，避免误推进；
 *  - 权益：只读用户本就能读的正文；连章时按服务端 403（MembershipRequiredError）为唯一裁判，
 *    下一章无权益即停止并提示，绝不朗读越权内容。
 */
import { create } from 'zustand';
import { splitSpeechChunks } from '../lib/mdToSpeech';
import {
  isTtsNative,
  ttsEnsurePermissions,
  ttsInitialize,
  ttsSpeak,
  ttsSpeakQueue,
  ttsStop,
  ttsSetRate,
  ttsUpdateMetadata,
  ttsRelease,
  ttsGetVoices,
  ttsSetVoice,
  ttsGetEngines,
  ttsSetEngine,
  onTtsChunk,
  onTtsQueueDone,
  onTtsTransport,
  type TtsChunkEvent,
  type TtsQueueDoneEvent,
  type TtsTransportAction,
  type TtsVoice,
  type TtsEngineInfo,
} from '@shared/api/tts';
import { fetchLearnChapter, MembershipRequiredError, type LearnChapterSummary } from '@shared/api/learn';
import { getCachedChapter, putCachedChapter, deleteCachedChapter } from '@shared/api/learnCache';
import { useLearnStore } from './learn-store';

const RATE_KEY = 'paipan.tts.rate';
const AUTONEXT_KEY = 'paipan.tts.autoNext';
const VOICE_KEY = 'paipan.tts.voice';
const ENGINE_KEY = 'paipan.tts.engine';
const PREVIEW_TEXT = '紫微斗数，天上的星辰，地上的命运。这是一段音色试听。';

/** 当前会话是否已向原生推送过引擎（避免每次播放都重建引擎） */
let engineApplied = false;

function loadEngine(): string | null {
  try {
    const v = localStorage.getItem(ENGINE_KEY);
    return v && v.length ? v : null;
  } catch {
    return null;
  }
}

/** 音色引用（name+locale+quality 三字段联合定位；name 单独不唯一） */
export type TtsVoiceRef = { name: string; locale: string; quality?: number };

function loadVoice(): TtsVoiceRef | null {
  try {
    const raw = localStorage.getItem(VOICE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o && typeof o.name === 'string' && o.name) return { name: o.name, locale: typeof o.locale === 'string' ? o.locale : '', quality: typeof o.quality === 'number' ? o.quality : undefined };
    return null;
  } catch {
    return null;
  }
}

function loadRate(): number {
  try {
    const v = Number(localStorage.getItem(RATE_KEY));
    return Number.isFinite(v) && v >= 0.5 && v <= 3 ? v : 1;
  } catch {
    return 1;
  }
}
function loadAutoNext(): boolean {
  try {
    return localStorage.getItem(AUTONEXT_KEY) === '1';
  } catch {
    return false;
  }
}

type TtsStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'ended';

interface TtsState {
  status: TtsStatus;
  supported: boolean | null;
  error: string;
  chapterId: number | null;
  chapterTitle: string;
  chunks: string[];
  cursor: number;
  total: number;
  rate: number;
  autoNext: boolean;
  voice: TtsVoiceRef | null; // 所选音色（null = 跟随系统默认）
  voices: TtsVoice[]; // 设备可用音色（refreshVoices 后填充）
  voicesLoaded: boolean;
  previewing: boolean;
  engine: string | null; // 所选 TTS 引擎包名（null = 系统默认）
  engines: TtsEngineInfo[]; // 设备已安装引擎列表
  enginesLoaded: boolean;

  // 运行时上下文（不入 UI 消费，仅内部用）
  order: number[];
  byId: Map<number, LearnChapterSummary>;
  token: string | null;
  memberLevel: string | null;
  currentMd: string;
  sessionId: number;

  configure(payload: { order: number[]; summaries: LearnChapterSummary[]; token: string | null; memberLevel: string | null }): void;
  start(chapterId: number, title: string, contentMd: string): Promise<void>;
  toggle(): void;
  stop(): void;
  nextChapter(): void;
  prevChapter(): void;
  setRate(rate: number): void;
  setAutoNext(v: boolean): void;
  setVoice(v: TtsVoiceRef | null): void;
  refreshVoices(): Promise<TtsVoice[]>;
  refreshEngines(): Promise<TtsEngineInfo[]>;
  setEngine(pkg: string | null): Promise<void>;
  preview(v?: TtsVoiceRef | null): Promise<void>;
}

let listenersBound = false;

function chapterIdFromHash(): number | null {
  const m = window.location.hash.match(/^#\/learn\/chapter\/(\d+)/);
  return m ? Number(m[1]) : null;
}

export const useTtsStore = create<TtsState>((set, get) => {
  // —— 内部推进 ——
  /** 从第 startIndex 句起，把本章剩余句子整段交给原生逐句队列（原生 onDone 推进，熄屏不断音） */
  const playFrom = (startIndex: number) => {
    const { chunks, chapterId, sessionId, rate } = get();
    if (startIndex >= chunks.length) {
      endChapter();
      return;
    }
    const ids = chunks.map((_, i) => `${sessionId}:${chapterId}:${i}`);
    set({ cursor: startIndex, status: 'playing', error: '' });
    void ttsSpeakQueue({ texts: chunks.slice(startIndex), ids: ids.slice(startIndex), rate, lang: 'zh-CN' });
  };

  const endChapter = () => {
    const { autoNext } = get();
    if (autoNext) {
      moveChapter(1);
    } else {
      set({ status: 'ended' });
    }
  };

  const begin = (id: number, title: string, md: string) => {
    // 新会话：sessionId 递增让上一段陈旧完成事件失效
    const sessionId = get().sessionId + 1;
    const chunks = splitSpeechChunks(md);
    set({ sessionId, chapterId: id, chapterTitle: title, currentMd: md, chunks, total: chunks.length, cursor: -1, error: '' });
    void ttsUpdateMetadata(title);
    if (chunks.length === 0) {
      endChapter();
      return;
    }
    playFrom(0);
  };

  const syncSelection = (id: number) => {
    useLearnStore.getState().select(id);
    if (chapterIdFromHash() !== id) window.location.hash = `#/learn/chapter/${id}`;
  };

  /** 连章：优先命中本地加密缓存，未命中回源；403 → 权益不足，停止并提示 */
  const loadAndStart = async (id: number) => {
    const { byId, token, memberLevel, order } = get();
    const summary = byId.get(id);
    if (!summary) {
      set({ status: 'ended' });
      return;
    }
    set({ status: 'loading', chapterId: id, chapterTitle: summary.title, chunks: [], total: 0, cursor: -1, error: '' });
    syncSelection(id);
    const ctx = { token, memberLevel };
    try {
      const cached = await getCachedChapter(id, summary.version, ctx);
      if (cached != null) {
        begin(id, summary.title, cached);
        return;
      }
      const d = await fetchLearnChapter(id, token);
      void putCachedChapter({ id, version: summary.version, effective_level: summary.effective_level }, d.content, ctx);
      if (get().chapterId !== id) return; // 期间被切走
      begin(id, summary.title, d.content);
    } catch (e) {
      if (e instanceof MembershipRequiredError) {
        void deleteCachedChapter(id);
        set({ status: 'ended', error: '下一章为会员内容，当前权益不足，已停止朗读' });
      } else {
        set({ status: 'ended', error: e instanceof Error ? e.message : String(e) });
      }
    }
    void order;
  };

  const moveChapter = (delta: number) => {
    const { order, chapterId } = get();
    if (chapterId == null || order.length === 0) {
      set({ status: 'ended' });
      return;
    }
    const idx = order.indexOf(chapterId);
    const nid = order[idx + delta];
    if (nid == null) {
      set({ status: 'ended' });
      return;
    }
    void loadAndStart(nid);
  };

  const hardStop = () => {
    const { sessionId } = get();
    set({ sessionId: sessionId + 1, status: 'idle', cursor: -1 });
    void ttsStop();
    void ttsRelease();
  };

  const onChunk = (e: TtsChunkEvent) => {
    // 试听完成（id 前缀 preview:）优先处理，不受章节播放状态影响
    if (e.id.startsWith('preview:')) {
      if (get().previewing) set({ previewing: false });
      return;
    }
    const { sessionId, chapterId, cursor, status } = get();
    if (status !== 'playing') return; // 暂停/停止期间的杂散完成事件不处理
    const prefix = `${sessionId}:${chapterId}:`;
    if (!e.id.startsWith(prefix)) return; // 陈旧/其它会话事件
    const idx = Number(e.id.slice(prefix.length));
    if (Number.isNaN(idx)) return;
    // 逐句推进已下沉到原生；JS 仅同步进度游标（仅向前，不回退）
    if (idx > cursor) set({ cursor: idx });
    if (e.type === 'error') {
      // 引擎出错：停掉原生队列，置暂停供用户重试（重播当前句）
      set({ status: 'paused', error: '朗读引擎出错，请重试' });
      void ttsStop();
    }
  };

  /** 原生把整章队列念完后回报 → 走章节结束逻辑（连读下一章或置 ended） */
  const onQueueDone = (e: TtsQueueDoneEvent) => {
    if (!e.lastId || e.lastId.startsWith('preview:')) return;
    const { sessionId, chapterId, status } = get();
    if (status !== 'playing') return; // 暂停/停止时的陈旧队列完成事件不推进
    if (!e.lastId.startsWith(`${sessionId}:${chapterId}:`)) return; // 陈旧/其它会话
    endChapter();
  };

  const onTransport = (a: TtsTransportAction) => {
    switch (a) {
      case 'play':
        get().toggle();
        break;
      case 'pause':
        if (get().status === 'playing') {
          set({ status: 'paused' });
          void ttsStop(); // 停引擎（onStop 不回报、服务保留供锁屏）
        }
        break;
      case 'next':
        moveChapter(1);
        break;
      case 'previous':
        moveChapter(-1);
        break;
      case 'stop':
        hardStop();
        break;
    }
  };

  /** 绑定逐句/锁屏事件监听（幂等）：configure 与 试听/取音色均可触发 */
  const bindListeners = () => {
    if (!listenersBound && isTtsNative()) {
      listenersBound = true;
      onTtsChunk(onChunk);
      onTtsQueueDone(onQueueDone);
      onTtsTransport(onTransport);
    }
  };

  /** 确保当前会话已把引擎绑定到原生并返回其可用性；若用户未显式选引擎且当前引擎不支持中文，
   *  则自动遍历已装引擎，绑定第一个支持中文的（同一引擎只推一次） */
  const ensureEngine = async (): Promise<{ supported?: boolean; permissionsOk?: boolean }> => {
    if (engineApplied) {
      return await ttsInitialize('zh-CN');
    }
    await ttsSetEngine(get().engine);
    engineApplied = true;
    let init = await ttsInitialize('zh-CN');
    // 未显式选引擎 && 当前引擎不支持中文 → 自动兜底：在已装引擎中找一个支持中文的绑定
    if (init?.supported === false && !get().engine) {
      const engs = get().engines.length ? get().engines : await ttsGetEngines().catch(() => [] as TtsEngineInfo[]);
      for (const e of engs) {
        await ttsSetEngine(e.package);
        init = await ttsInitialize('zh-CN');
        if (init?.supported !== false) {
          set({ engine: e.package }); // 仅会话内记忆，不写 localStorage，用户仍可手动改
          break;
        }
      }
      if (init?.supported === false) {
        await ttsSetEngine(null); // 都不支持中文：回落系统默认引擎
      }
    }
    await ttsSetRate(get().rate);
    return init;
  };

  return {
    status: 'idle',
    supported: null,
    error: '',
    chapterId: null,
    chapterTitle: '',
    chunks: [],
    cursor: -1,
    total: 0,
    rate: loadRate(),
    autoNext: loadAutoNext(),
    voice: loadVoice(),
    voices: [],
    voicesLoaded: false,
    previewing: false,
    engine: loadEngine(),
    engines: [],
    enginesLoaded: false,
    order: [],
    byId: new Map(),
    token: null,
    memberLevel: null,
    currentMd: '',
    sessionId: 0,

    configure: ({ order, summaries, token, memberLevel }) => {
      set({ order, byId: new Map(summaries.map((s) => [s.id, s])), token, memberLevel });
      bindListeners();
    },

    start: async (chapterId, title, contentMd) => {
      if (!isTtsNative()) return;
      bindListeners();
      // 同一章且处于暂停 → 继续（从当前句重起队列）；同章其它态 → 从头
      if (get().chapterId === chapterId && get().status === 'paused') {
        playFrom(get().cursor);
        return;
      }
      await ttsEnsurePermissions();
      const init = await ensureEngine();
      const supported = init?.supported !== false;
      set({ supported });
      if (!supported) {
        set({ error: '未检测到可用的中文语音引擎，请在「我的 → 听书音色」的引擎下拉中选择支持中文的引擎' });
        return;
      }
      await ttsSetRate(get().rate);
      await ttsSetVoice(get().voice || null);
      begin(chapterId, title, contentMd);
    },

    toggle: () => {
      const { status, chapterId, currentMd, chapterTitle } = get();
      if (status === 'playing') {
        set({ status: 'paused' });
        void ttsStop(); // 引擎无 pause：停当前句，cursor 不动，服务保留
        return;
      }
      if (status === 'paused') {
        playFrom(get().cursor); // 从当前句重起逐句队列
        return;
      }
      // idle/ended：重播当前章
      if (chapterId != null && currentMd) {
        void get().start(chapterId, chapterTitle, currentMd);
      }
    },

    stop: () => hardStop(),
    nextChapter: () => moveChapter(1),
    prevChapter: () => moveChapter(-1),

    setRate: (rate) => {
      set({ rate });
      try {
        localStorage.setItem(RATE_KEY, String(rate));
      } catch {
        // 忽略
      }
      void ttsSetRate(rate);
    },

    setAutoNext: (v) => {
      set({ autoNext: v });
      try {
        localStorage.setItem(AUTONEXT_KEY, v ? '1' : '0');
      } catch {
        // 忽略
      }
    },

    setVoice: (v) => {
      set({ voice: v && v.name ? v : null });
      try {
        localStorage.setItem(VOICE_KEY, v && v.name ? JSON.stringify(v) : '');
      } catch {
        // 忽略
      }
      void ttsSetVoice(v && v.name ? v : null); // 若引擎已就绪，下一句自动应用
    },

    /** 取设备音色列表（进页时调用）：仅原生；初始化引擎后拉 getVoices */
    refreshVoices: async () => {
      if (!isTtsNative()) {
        set({ voicesLoaded: true });
        return [];
      }
      bindListeners();
      await ttsEnsurePermissions();
      const init = await ensureEngine();
      set({ supported: init?.supported !== false });
      const voices = await ttsGetVoices();
      set({ voices, voicesLoaded: true });
      return voices;
    },

    /** 拉设备已安装引擎列表（不依赖当前引擎绑定） */
    refreshEngines: async () => {
      if (!isTtsNative()) {
        set({ enginesLoaded: true });
        return [];
      }
      const engines = await ttsGetEngines();
      set({ engines, enginesLoaded: true });
      return engines;
    },

    /** 切换引擎：持久化 + 重置推送标记 + 重新绑定并刷新音色（不同引擎音色不互通） */
    setEngine: async (pkg) => {
      const npkg = pkg || null;
      set({ engine: npkg, voices: [], voicesLoaded: false, voice: null }); // 音色随引擎失效，回默认
      try {
        localStorage.setItem(ENGINE_KEY, npkg || '');
        localStorage.removeItem(VOICE_KEY);
      } catch {
        // 忽略
      }
      engineApplied = false; // 下次 ensureEngine 推送新引擎
      await get().refreshVoices();
    },

    /** 试听一段样例（用当前或指定音色）；不拉前台服务，完成后自动回落 previewing */
    preview: async (v) => {
      if (!isTtsNative()) return;
      bindListeners();
      const useVoice = v === undefined ? get().voice : v;
      await ttsEnsurePermissions();
      const init = await ensureEngine();
      if (init?.supported === false) {
        set({ supported: false, error: '未检测到可用的中文语音引擎' });
        return;
      }
      // 试听会打断章节朗读：若正在播放则置为暂停（可点继续从当前句重播）
      if (get().status === 'playing') set({ status: 'paused' });
      await ttsSetVoice(useVoice && useVoice.name ? useVoice : null);
      await ttsSetRate(get().rate);
      set({ previewing: true });
      void ttsStop(); // 切断上一次试听
      void ttsSpeak({ text: PREVIEW_TEXT, id: `preview:${Date.now()}`, rate: get().rate, lang: 'zh-CN', preview: true });
      // 安全兵：若 done 事件丢失，12s 后强制回落
      setTimeout(() => {
        if (get().previewing) set({ previewing: false });
      }, 12000);
    },
  };
});
